import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input } from 'antd';
import dayjs from 'dayjs';
import { SectionPage } from './SectionPage';
import { useRegisterDirtyForm } from '@/components/settings/DirtyFormGuard';
import { COPY } from '@/lib/copyTokens';
import type { TenantSettings } from '@/types/tenant';
import { useSectionTenantSettings } from './useSectionTenantSettings';
import { ClientStatusCard } from './ClientStatusCard';

const BUSINESS_FIELDS = [
  'website',
  'google_domain_url',
  'locality',
  'region',
  'postal_code',
  'street_address',
  'country',
  'contact',
  'disallowed_locations',
] as const satisfies ReadonlyArray<keyof TenantSettings>;

type BusinessValues = Pick<TenantSettings, (typeof BUSINESS_FIELDS)[number]>;

function formatDate(value: string | undefined) {
  return value ? dayjs(value).format('D MMM YYYY') : COPY.sections.businessInfo.notSavedYet;
}

/** Business info landing: page name + one-line welcome blurb + the basics. */
export default function BusinessInfoSection() {
  const [form] = Form.useForm<BusinessValues>();
  const [isDirty, setIsDirty] = useState(false);
  const { tenantId, tenantSettings, isLoading, isSaving, save } = useSectionTenantSettings();

  useEffect(() => {
    if (tenantId) {
      form.setFieldsValue({ website: tenantId, ...tenantSettings });
      setIsDirty(false);
    }
  }, [form, tenantId, tenantSettings]);

  const submit = useCallback(async () => {
    const values = await form.validateFields();
    const succeeded = await save(
      Object.fromEntries(BUSINESS_FIELDS.map((field) => [field, values[field]])) as BusinessValues,
    );
    if (succeeded) setIsDirty(false);
    return succeeded;
  }, [form, save]);

  const discard = useCallback(() => {
    form.setFieldsValue({ website: tenantId ?? undefined, ...tenantSettings });
    setIsDirty(false);
  }, [form, tenantId, tenantSettings]);

  useRegisterDirtyForm(
    useMemo(
      () => ({ isDirty: () => isDirty, save: submit, discard }),
      [discard, isDirty, submit],
    ),
  );

  if (!tenantId) {
    return (
      <SectionPage sectionId="business" blurb={COPY.businessBlurb}>
        <p className="text-sm text-ink-2">{COPY.chooseEntity}</p>
      </SectionPage>
    );
  }

  return (
    <SectionPage sectionId="business" blurb={COPY.businessBlurb}>
      <Form<BusinessValues>
        form={form}
        layout="vertical"
        className="max-w-4xl space-y-9"
        onFinish={submit}
        onValuesChange={() => setIsDirty(true)}
      >
        <section aria-labelledby="workspace-domain-heading" className="space-y-4">
          <h3 id="workspace-domain-heading" className="text-sm font-semibold tracking-tight text-ink">
            {COPY.sections.businessInfo.workspaceDomain}
          </h3>
          <Form.Item
            name="website"
            label={COPY.workspaceId.label}
            tooltip={COPY.workspaceId.tooltip}
            extra={COPY.workspaceId.extra}
            rules={[{ required: true, message: COPY.workspaceId.required }]}
          >
            <Input className="w-full font-mono" />
          </Form.Item>
          <Form.Item
            name="google_domain_url"
            label={COPY.sections.businessInfo.googleDomainUrl}
            extra={COPY.sections.businessInfo.googleDomainUrlHelp}
          >
            <Input type="url" className="w-full" />
          </Form.Item>
        </section>

        <section aria-labelledby="location-heading" className="space-y-4">
          <h3 id="location-heading" className="text-sm font-semibold tracking-tight text-ink">
            {COPY.sections.businessInfo.location}
          </h3>
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_18ch]">
            <Form.Item name="locality" label={COPY.sections.businessInfo.locality}>
              <Input />
            </Form.Item>
            <Form.Item name="region" label={COPY.sections.businessInfo.region}>
              <Input maxLength={18} placeholder={COPY.sections.businessInfo.regionPlaceholder} />
            </Form.Item>
          </div>
          <div className="grid gap-4 md:grid-cols-[18ch_minmax(0,1fr)]">
            <Form.Item name="postal_code" label={COPY.sections.businessInfo.postalCode}>
              <Input maxLength={18} />
            </Form.Item>
            <Form.Item name="street_address" label={COPY.sections.businessInfo.streetAddress}>
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="country" label={COPY.sections.businessInfo.country}>
            <Input className="w-full" />
          </Form.Item>
          <Form.Item
            name="contact"
            label={COPY.sections.businessInfo.contact}
            extra={COPY.sections.businessInfo.contactHelp}
          >
            <Input.TextArea rows={4} className="w-full" />
          </Form.Item>
          <Form.Item
            name="disallowed_locations"
            label={COPY.sections.businessInfo.disallowedLocations}
            extra={COPY.sections.businessInfo.disallowedLocationsHelp}
          >
            <Input.TextArea rows={4} className="w-full" />
          </Form.Item>
        </section>

        <p className="text-xs text-ink-3">
          <time dateTime={tenantSettings?.updated_at}>
            {COPY.sections.businessInfo.savedAt(formatDate(tenantSettings?.updated_at))}
          </time>
          {' · '}
          <time dateTime={tenantSettings?.created_at}>
            {COPY.sections.businessInfo.createdAt(formatDate(tenantSettings?.created_at))}
          </time>
        </p>

        <Button type="primary" htmlType="submit" loading={isLoading || isSaving}>
          {COPY.cards.dirtyGuard.save}
        </Button>
      </Form>
      {/* Staff only; confirms and writes on its own, never part of the Save above. */}
      <ClientStatusCard />
    </SectionPage>
  );
}
